//
// Reputation tier → small dot indicator color.
//
// The badge background stays on-brand (Chakra Badge default from the QIE
// theme). We differentiate tiers with a small colored dot rendered inside
// the badge so users can distinguish tiers at a glance without breaking
// the visual brand.
//
// Single source of truth so badges + tab headers + tooltips stay in sync.
// Fallback = neutral gray for unknown tiers.
//
import type { ReputationTier } from 'lib/api/useReputationScore';

export const REPUTATION_TIER_ORDER: Array<ReputationTier> = [ 'New', 'Active', 'Trusted', 'Strong', 'Elite' ];

// Dot colors — Chakra colorScheme names so they respect light/dark theme
// automatically (e.g. `green.400` in dark mode, `green.500` in light).
const TIER_DOT_SCHEME: Record<string, string> = {
  New: 'gray',
  Active: 'blue',
  Trusted: 'green',
  Strong: 'teal',
  Elite: 'purple',
};

export function getTierDotScheme(tier: ReputationTier | undefined | null): string {
  if (!tier) return 'gray';
  return TIER_DOT_SCHEME[tier] ?? 'gray';
}

// Kept as an alias for the AddressReputation hero (which uses the scheme
// on progress bars + big score text — those elements aren't overridden by
// the theme, so colorScheme actually works there).
export const getTierColorScheme = getTierDotScheme;

// Score ranges per tier — used purely for display ("50 – 69 range"),
// not for computing the tier itself. That's done server-side.
export function getTierRangeLabel(tier: ReputationTier | undefined | null): string | null {
  switch (tier) {
    case 'New': return '0 – 29';
    case 'Active': return '30 – 49';
    case 'Trusted': return '50 – 69';
    case 'Strong': return '70 – 84';
    case 'Elite': return '85 – 100';
    default: return null;
  }
}